import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../App';

const fmt = n => (typeof n === 'number' ? `${n.toFixed(2)} €` : '—');
const fmtDate = s => s ? s.split('-').reverse().join('/') : '—';
const round2 = n => Math.round(n * 100) / 100;
const CHANNEL_LABELS = {
  airbnb: 'Airbnb', booking: 'Booking', vrbo: 'Vrbo',
  expedia: 'Expedia', agoda: 'Agoda', direct: 'Direct'
};
const channelLabel = c => CHANNEL_LABELS[c?.toLowerCase()] ?? c ?? '—';

// Cellule éditable : clic sur le crayon → input inline
function EditableCell({ value, fieldLabel, onSave, style }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef();

  const open = () => { setDraft(typeof value === 'number' ? value.toFixed(2) : '0'); setEditing(true); };
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const confirm = () => {
    const v = parseFloat(draft.replace(',', '.'));
    if (!isNaN(v)) onSave(round2(v));
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <td style={style}>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirm(); if (e.key === 'Escape') cancel(); }}
            style={{ width:70, padding:'2px 4px', border:'1.5px solid #4F6EE8', borderRadius:4, fontSize:13 }}
          />
          <button onClick={confirm} style={{ background:'#4F6EE8', color:'white', border:'none', borderRadius:4, padding:'2px 6px', cursor:'pointer', fontSize:12 }}>✓</button>
          <button onClick={cancel}  style={{ background:'#eee', border:'none', borderRadius:4, padding:'2px 6px', cursor:'pointer', fontSize:12 }}>✕</button>
        </div>
      </td>
    );
  }

  return (
    <td style={style}>
      <span style={{ display:'flex', alignItems:'center', gap:4 }}>
        {fmt(value)}
        <button
          onClick={open}
          title={`Modifier ${fieldLabel}`}
          style={{ background:'none', border:'none', cursor:'pointer', opacity:0.4, fontSize:11, padding:0, lineHeight:1 }}
        >✏️</button>
      </span>
    </td>
  );
}

function Dashboard({ password }) {
  const now = new Date();
  const [month, setMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [allTime, setAllTime] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filter, setFilter] = useState('all');
  const [payments, setPayments] = useState({});
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = allTime ? `/hostex/reservations` : `/hostex/reservations?month=${month}`;
      const [resaRes, payRes, ovrRes] = await Promise.all([
        apiFetch(url, password),
        apiFetch('/payments', password),
        apiFetch('/overrides', password)
      ]);
      const resaData = await resaRes.json();
      const payData  = await payRes.json();
      const ovrData  = await ovrRes.json();
      if (!resaRes.ok) throw new Error(resaData.error ?? 'Erreur API Hostex');
      setReservations(resaData.reservations);
      setPayments(payData);
      setOverrides(ovrData);
      const props = [...new Set(resaData.reservations.map(r => r.property))].filter(Boolean);
      setProperties(props);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [month, allTime, password]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Applique les overrides et recalcule les montants dérivés
  const applyOverrides = (r) => {
    const ov = overrides[r.id];
    if (!ov) return r;
    const accommodation      = ov.accommodation      ?? r.accommodation;
    const cleaning_fee       = ov.cleaning_fee       ?? r.cleaning_fee;
    const platform_commission = ov.platform_commission ?? r.platform_commission;
    const a_facturer         = ov.a_facturer         ?? r.a_facturer ?? 0;
    const baseComm    = round2(accommodation + (r.owner_upsells ?? 0) - platform_commission);
    const maCommission = round2(baseComm * 0.20);
    const recuAirbnb  = round2(cleaning_fee + maCommission);
    const monTotal    = round2(recuAirbnb + a_facturer);
    const netProp     = round2(accommodation + cleaning_fee + (r.owner_upsells ?? 0) + (r.a_facturer ?? 0) - platform_commission);
    const netReel     = round2(netProp - monTotal);
    return { ...r, accommodation, cleaning_fee, platform_commission, a_facturer,
             base_commission: baseComm, ma_commission: maCommission,
             recu_airbnb: recuAirbnb, mon_total: monTotal,
             net_proprietaire: netProp, net_reel_proprietaire: netReel,
             is_overridden: true };
  };

  const saveOverride = async (id, field, value) => {
    const newOvr = { ...(overrides[id] || {}), [field]: value };
    setOverrides(prev => ({ ...prev, [id]: newOvr }));
    await apiFetch('/overrides', password, {
      method: 'POST',
      body: JSON.stringify({ id, [field]: value })
    });
  };

  const togglePaid = async (id) => {
    const newVal = !payments[id];
    setPayments(prev => ({ ...prev, [id]: newVal }));
    await apiFetch(`/payments/${id}`, password, {
      method: 'POST',
      body: JSON.stringify({ paid: newVal })
    });
  };

  const displayed = filter === 'all'
    ? reservations
    : reservations.filter(r => r.property === filter);

  const active = displayed.filter(r => !r.cancelled && !r.warning);
  const T = active.map(applyOverrides).reduce((acc, r) => ({
    accommodation:       acc.accommodation       + (r.accommodation ?? 0),
    cleaning_fee:        acc.cleaning_fee        + (r.cleaning_fee ?? 0),
    owner_upsells:       acc.owner_upsells       + (r.owner_upsells ?? 0),
    platform_commission: acc.platform_commission + (r.platform_commission ?? 0),
    net_proprietaire:    acc.net_proprietaire    + (r.net_proprietaire ?? 0),
    net_reel:            acc.net_reel            + (r.net_reel_proprietaire ?? 0),
    ma_commission:       acc.ma_commission       + (r.ma_commission ?? 0),
    recu_airbnb:         acc.recu_airbnb         + (r.recu_airbnb ?? 0),
    a_facturer:          acc.a_facturer          + (r.a_facturer ?? 0),
    mon_total:           acc.mon_total           + (r.mon_total ?? 0),
  }), { accommodation:0, cleaning_fee:0, owner_upsells:0, platform_commission:0,
        net_proprietaire:0, net_reel:0, ma_commission:0, recu_airbnb:0, a_facturer:0, mon_total:0 });

  const thProp = { background: '#f5f8ff' };
  const thCo   = { background: '#f5fff8' };
  const thFact = { background: '#fff8e6' };

  return (
    <div className="section">
      <div className="toolbar">
        <div className="toolbar-left">
          <label>Mois :</label>
          <input
            type="month" value={month}
            onChange={e => { setMonth(e.target.value); setAllTime(false); }}
            className="input-sm" disabled={allTime}
          />
          <button
            className={`tab${allTime ? ' active' : ''}`}
            style={{ fontSize:13, padding:'6px 14px' }}
            onClick={() => setAllTime(v => !v)}
          >{allTime ? '✓ Toutes' : 'Toutes'}</button>
        </div>
        <div className="toolbar-right">
          <label>Logement :</label>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="input-sm">
            <option value="all">Tous</option>
            {properties.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {loading ? (
        <div className="loading">Chargement des réservations...</div>
      ) : (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th rowSpan={2}>Logement</th>
                <th rowSpan={2}>Voyageur</th>
                <th rowSpan={2}>Check-in</th>
                <th rowSpan={2}>Check-out</th>
                <th rowSpan={2}>Plateforme</th>
                <th rowSpan={2}>Statut</th>
                <th colSpan={6} style={{ textAlign:'center', background:'#f0fff4', borderBottom:'1px solid #9ae6b4' }}>
                  Co-hôte
                </th>
                <th colSpan={6} style={{ textAlign:'center', background:'#eef4ff', borderBottom:'1px solid #c5d6f5' }}>
                  Propriétaire
                </th>
              </tr>
              <tr>
                <th style={thCo}>Comm.</th>
                <th style={thCo}>Ménage</th>
                <th style={thCo}>Reçu Airbnb</th>
                <th style={thCo}>Payé ✓</th>
                <th style={thFact} title="Services co-hôte non payés par Airbnb — à facturer au client">Upsells</th>
                <th style={thFact} title="Montant à facturer séparément au client">Différence à facturer</th>

                <th style={thProp}>Vente</th>
                <th style={thProp}>Ménage</th>
                <th style={thProp}>Upsells</th>
                <th style={thProp}>Comm. plat.</th>
                <th style={thProp}>Net reçu</th>
                <th style={{ ...thProp, color:'#2b6cb0', fontWeight:800 }} title="Net reçu − ma commission">Net réel</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={17} style={{ textAlign:'center', color:'#999', padding:'32px' }}>
                    Aucune réservation pour cette période
                  </td>
                </tr>
              ) : displayed.map(r => {
                if (r.cancelled) {
                  return (
                    <tr key={r.id} style={{ opacity:0.45, background:'#fafafa' }}>
                      <td>{r.property}</td>
                      <td>{r.guest}</td>
                      <td>{fmtDate(r.check_in)}</td>
                      <td>{fmtDate(r.check_out)}</td>
                      <td><span className="badge-channel">{channelLabel(r.channel)}</span></td>
                      <td><span className="badge-cancelled">Annulé</span></td>
                      <td colSpan={11} style={{ color:'#aaa', fontStyle:'italic' }}>—</td>
                    </tr>
                  );
                }
                if (r.warning) {
                  return (
                    <tr key={r.id} className="row-warning">
                      <td>{r.property}</td>
                      <td>{r.guest}</td>
                      <td>{fmtDate(r.check_in)}</td>
                      <td>{fmtDate(r.check_out)}</td>
                      <td><span className="badge-channel">{channelLabel(r.channel)}</span></td>
                      <td><span className="badge-warning">⚠️</span></td>
                      <td colSpan={11} style={{ color:'#c0392b', fontSize:13 }}>{r.warning}</td>
                    </tr>
                  );
                }

                const isPaid = !!payments[r.id];
                const row = applyOverrides(r);
                return (
                  <tr key={r.id} style={isPaid ? { background:'#f0fff4' } : {}}>
                    <td>{row.property}</td>
                    <td>
                      {row.guest}
                      {row.is_weekly_split && (
                        <span style={{ marginLeft:6, fontSize:11, background:'#e9d8fd', color:'#553c9a', padding:'1px 6px', borderRadius:8 }}>
                          S{row.week_num}/{row.total_weeks}
                        </span>
                      )}
                      {row.is_overridden && <span title="Valeurs modifiées manuellement" style={{ marginLeft:4, fontSize:11 }}>✏️</span>}
                    </td>
                    <td>{fmtDate(row.check_in)}</td>
                    <td>{fmtDate(row.check_out)}</td>
                    <td><span className="badge-channel">{channelLabel(row.channel)}</span></td>
                    <td><span className="badge-active">Confirmé</span></td>

                    {/* Co-hôte */}
                    <td style={{ background:'#f5fff8' }}>{fmt(row.ma_commission)}</td>
                    <EditableCell value={row.cleaning_fee} fieldLabel="Ménage"
                      onSave={v => saveOverride(r.id, 'cleaning_fee', v)}
                      style={{ background:'#f5fff8' }} />
                    <td style={{ background:'#f5fff8', fontWeight:700, color:'#276749' }}>{fmt(row.recu_airbnb)}</td>
                    <td style={{ background:'#f5fff8', textAlign:'center' }}>
                      <input type="checkbox" checked={isPaid} onChange={() => togglePaid(r.id)}
                        style={{ width:18, height:18, cursor:'pointer', accentColor:'#38a169' }} />
                    </td>
                    <td style={{ background:'#fff8e6', color: row.a_facturer > 0 ? '#b7791f' : '#ccc' }}>
                      {row.a_facturer > 0
                        ? <span title={row.a_facturer_items?.join(', ')}>{fmt(row.a_facturer)} 🔍</span>
                        : '—'}
                    </td>
                    <td style={{ background:'#fff8e6', fontWeight: row.a_facturer > 0 ? 700 : 400, color: row.a_facturer > 0 ? '#b7791f' : '#ccc' }}>
                      {row.a_facturer > 0 ? fmt(row.a_facturer) : '—'}
                    </td>

                    {/* Propriétaire */}
                    <EditableCell value={row.accommodation} fieldLabel="Vente"
                      onSave={v => saveOverride(r.id, 'accommodation', v)}
                      style={{ background:'#f9fbff' }} />
                    <EditableCell value={row.cleaning_fee} fieldLabel="Ménage proprio"
                      onSave={v => saveOverride(r.id, 'cleaning_fee', v)}
                      style={{ background:'#f9fbff' }} />
                    <td style={{ background:'#f9fbff' }}>
                      {row.owner_upsells > 0
                        ? <span title={row.owner_upsell_items?.join(', ')}>{fmt(row.owner_upsells)} 🔍</span>
                        : '—'}
                    </td>
                    <EditableCell value={row.platform_commission} fieldLabel="Comm. plateforme"
                      onSave={v => saveOverride(r.id, 'platform_commission', v)}
                      style={{ background:'#f9fbff' }} />
                    <td style={{ background:'#f9fbff', fontWeight:600 }}>{fmt(row.net_proprietaire)}</td>
                    <td style={{ background:'#eaf2ff', fontWeight:800, color:'#2b6cb0' }}>{fmt(row.net_reel_proprietaire)}</td>
                  </tr>
                );
              })}
            </tbody>
            {active.length > 0 && (
              <tfoot>
                <tr className="totals-row">
                  <td colSpan={6}><strong>TOTAUX ({active.length} résa)</strong></td>
                  <td>{fmt(T.ma_commission)}</td>
                  <td>{fmt(T.cleaning_fee)}</td>
                  <td><strong>{fmt(T.recu_airbnb)}</strong></td>
                  <td></td>
                  <td style={{ color:'#b7791f' }}>{T.a_facturer > 0 ? fmt(T.a_facturer) : '—'}</td>
                  <td style={{ color:'#b7791f', fontWeight:700 }}>{T.a_facturer > 0 ? fmt(T.a_facturer) : '—'}</td>
                  <td>{fmt(T.accommodation)}</td>
                  <td>{fmt(T.cleaning_fee)}</td>
                  <td>{fmt(T.owner_upsells)}</td>
                  <td>{fmt(T.platform_commission)}</td>
                  <td><strong>{fmt(T.net_proprietaire)}</strong></td>
                  <td style={{ fontWeight:800, color:'#2b6cb0' }}>{fmt(T.net_reel)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
